import { useState } from "react";
import {
  Box,
  Button as MuiButton,
  InputBase,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/material";
import { format, getUnixTime } from "date-fns";
import { useNavigate } from "react-router-dom";

import { SearchIcon, ExportIcon } from "components/Icons";
import { Typography } from "components/Typography";
import { TextInput } from "components/TextInput";
import { DataGrid } from "components/DataGrid";
import { DateRangePicker } from "components/DateRangePicker";
import { useGetTransactionsQuery } from "api/transactions";
import { exportCSV } from "utils/exportCsv";
import { formatToNaira } from "utils/formatCurrency";
import { useGetBusinessesQuery } from "api/business";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  border: "1.5px solid #EFF1F4",
  borderRadius: "8px",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));

const Wrapper = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

const Button = styled(MuiButton)(() => ({
  borderColor: "#EFF1F4",
  color: "black",
  height: "40px",
  textTransform: "capitalize",
}));

const columns = [
  { field: "businessName", headerName: "BUSINESS NAME", flex: 1 },
  {
    field: "originBusinessName",
    headerName: "ORIGINAL BUSINESS NAME",
    flex: 1,
  },
  {
    field: "amount",
    headerName: "TOTAL AMOUNT",
    flex: 1,
    valueFormatter: ({ value }) => formatToNaira(value),
  },
  { field: "debitDescription", headerName: "DESCRIPTION", flex: 1 },
  {
    field: "totalDetails",
    headerName: "TOTAL COUNT",
    flex: 1,
  },
  {
    field: "totalSuccess",
    headerName: "SUCCESSFUL COUNT",
    flex: 1,
  },
  {
    field: "totalFailed",
    headerName: "FAILED COUNT",
    flex: 1,
  },
  {
    field: "createdOn",
    headerName: "DATE & TIME",
    flex: 1,
    valueFormatter: ({ value }) => {
      if (!value) return "—";
      return format(new Date(value * 1000), "dd/MM/yyyy hh:mm a");
    },
  },
];

const PAGE_SIZE = 1000;
const SORT_ORDER = "date-desc";

const DEFAULT_OPTION = {
  label: "All",
  value: "",
};

export const Transactions = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [businessId, setBusinessId] = useState(DEFAULT_OPTION.value);

  const { data, isLoading } = useGetTransactionsQuery({
    search,
    from,
    to,
    businessId,
    limit: PAGE_SIZE,
    sort: SORT_ORDER,
  });
  const { data: businesses = [], isLoading: isLoadingBusinesses } =
    useGetBusinessesQuery();

  const businessesOptions = businesses.map((item) => ({
    label: item.name,
    value: item.businessId,
  }));

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  const handleSelectRange = (range) => {
    setFrom(getUnixTime(range.startDate));
    setTo(getUnixTime(range.endDate));
  };

  const handleExportCSV = () => {
    if (!data?.transactions?.length) return;

    const exportableRows = data.transactions.map((row) => ({
      "Transaction ID": row.transId,
      "Business Name": row.businessName,
      "Original Business Name": row.originBusinessName,
      "Total Amount": row.amount,
      Description: row.debitDescription || "",
      Date: row.createdOn
        ? format(new Date(row.createdOn * 1000), "dd/MM/yyyy hh:mm a")
        : "—",
      "Total Failed": row.totalFailed,
      "Total Success": row.totalSuccess,
    }));

    exportCSV(exportableRows, "transactions.csv");
  };

  const handleRowClick = (params) => {
    const row = params.row;
    navigate(`/transactions/${row.transId}`);
  };

  return (
    <Box>
      <Wrapper sx={{ justifyContent: "space-between", marginBottom: 3 }}>
        <Wrapper>
          <Typography variant="dashboardHeading">Transactions</Typography>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search"
              inputProps={{ "aria-label": "search" }}
              value={search}
              onChange={handleSearch}
            />
          </Search>
        </Wrapper>

        <Wrapper sx={{ columnGap: "8px" }}>
          <Autocomplete
            disablePortal
            options={[DEFAULT_OPTION, ...businessesOptions]}
            sx={{ minWidth: 300 }}
            autoHighlight
            loading={isLoadingBusinesses}
            onChange={(_, event) => {
              setBusinessId(event?.value || "");
            }}
            getOptionLabel={(option) => option.label || ""}
            renderInput={(params) => (
              <TextInput
                {...params}
                placeholder="Filter by Business"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "new-password", // disable autocomplete and autofill
                  style: { paddingTop: 0, paddingBottom: 0 },
                }}
              />
            )}
          />
          <DateRangePicker handleSelectRange={handleSelectRange} />
          <Button variant="outlined" onClick={handleExportCSV}>
            <Wrapper>
              <ExportIcon />
              <div style={{ marginTop: 5 }}>Export CSV</div>
            </Wrapper>
          </Button>
        </Wrapper>
      </Wrapper>

      <DataGrid
        rows={data?.transactions || []}
        getRowId={(row) => row.transId}
        onRowClick={handleRowClick}
        columns={columns}
        loading={isLoading}
        disableColumnMenu
        disableRowSelectionOnClick
        pagination
        pageSize={PAGE_SIZE}
      />
    </Box>
  );
};
